import React, { useEffect, useRef, useState } from "react";
import axios from 'axios';
import { toast, ToastContainer } from "react-toastify";
import useHandleErrors from "../../utils/user.utils";
import moment from "moment";
import { useUser } from '../../utils/userContext';


const UploadCenter = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('');
    const [filename, setFilename] = useState('');
    const handleErrors = useHandleErrors();
    const [note, setNote] = useState('Note about uploading bulk files goes here');
    const token = localStorage.getItem("token");
    const [files, setFiles] = useState([]);
    const { user } = useUser();
    const hasFetchedRef = useRef(false);


    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFilename(file.name);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSelectedFile(null);
        setFilename('');
        switch (tabId) {
            case 'bulkPayslips':
                setNote(`Please upload files in the format: monthyear.zip (e.g., june${new Date().getFullYear()}.zip)`);
                break;
            case 'bulkInsurances':
                setNote(`Please upload files in the format: Insurance${new Date().getFullYear()}.zip (e.g., SpouseInsurance${new Date().getFullYear()}.zip)`);
                break;
            case 'bulkTaxes':
                setNote(`Please upload files in the format: Tax${new Date().getFullYear()}.zip (e.g., PersonalTax${new Date().getFullYear()}.zip)`);
                break;
            default:
                setNote('Note about uploading bulk files goes here');
                break;
        }
    };

    const validateFilename = (filename, tab) => {
        const currentYear = new Date().getFullYear();

        switch (tab) {
            case 'bulkPayslips':
                // Payslip validation: 'month year'
                return /^[a-zA-Z]+ ?\d{4}\.zip$/.test(filename);

            case 'bulkInsurances':
                // Insurances validation: Any prefix + 'insurance' + year (e.g., 'spouseinsurance2024.zip')
                return /^[a-zA-Z]*insurance\d{4}\.zip$/i.test(filename);

            case 'bulkTaxes':
                // Taxes validation: Any prefix + 'tax' or 'personaltax' + year (e.g., 'selftax2024.zip', 'personaltax2024.zip')
                return /^[a-zA-Z]*(tax|personaltax)\d{4}\.zip$/i.test(filename);

            default:
                return false;
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error("Please select a file to upload.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        const filename = selectedFile.name;
        if (!validateFilename(filename, activeTab)) {
            switch (activeTab) {
                case 'bulkPayslips':
                    toast.error("Filename should be in format 'month year'", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    break;
                case 'bulkInsurances':
                    toast.error(`Filename should be 'Insurance${new Date().getFullYear()}'`, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    break;
                case 'bulkTaxes':
                    toast.error(`Filename should be 'Tax${new Date().getFullYear()}'`, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    break;
                default:
                    toast.error("Invalid filename format for the selected tab.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    break;
            }
            return;
        }


        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("UpdatedBy", user?.EmployeeId);

        let apiUrl;
        if (activeTab === 'bulkPayslips') {
            const path = "/uploadPayslips";
            const URL = process.env.REACT_APP_BE_URL + path;
            apiUrl = URL;
        } else {
            const path = "/uploadDocuments";
            const URL = process.env.REACT_APP_BE_URL + path;
            apiUrl = URL;
        }

        setUploading(true);

        try {
            const response = await axios.post(apiUrl, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                toast.success("File uploaded successfully!", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setSelectedFile(null);
                setFilename('');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setUploading(false);
        }
    };

    const fetchFiles = async () => {
        setUploading(true);
        try {
            const path = "/getZipDocuments";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            if (response.status === 200) {
                setFiles(response.data.zipDocuments);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    setFiles([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchFiles();
            hasFetchedRef.current = true;
        }
    }, []);


    return (
        <div>
            {uploading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Upload Center</h3>
                        </div>
                        <div className="col-auto">
                            <a
                                href={'/Employees'}
                                className="btn btn-primary mt-lg-0 mt-4"
                            >
                                <i className="ri-user-star-line"></i> View All Employees
                            </a>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-8">
                            <div className="card mb-4">
                                <div className="card-body bulk-upload-files">
                                    <div className="container">
                                        <div className="row vh-25 justify-content-center align-items-center">
                                            <div className="col-auto">
                                                <div className="no-data-wrapper">
                                                    <div className="no-data-found">
                                                        <i className="ri-folder-zip-fill"></i>
                                                        <div className="bounce">
                                                            <i className="ri-inbox-unarchive-fill"></i>
                                                        </div>
                                                        <p>{note}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <ul
                                        className="nav nav-pills tab-pills justify-content-center custom-pill mt-5 mb-4"
                                        role="tablist"
                                    >
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'bulkPayslips' ? 'active' : ''}`}
                                                onClick={() => handleTabChange('bulkPayslips')}
                                                role="tab"
                                            >
                                                Payslips
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'bulkInsurances' ? 'active' : ''}`}
                                                onClick={() => handleTabChange('bulkInsurances')}
                                                role="tab"
                                            >
                                                Insurances
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'bulkTaxes' ? 'active' : ''}`}
                                                onClick={() => handleTabChange('bulkTaxes')}
                                                role="tab"
                                            >
                                                Taxes
                                            </a>
                                        </li>
                                    </ul>

                                    <div className="tab-content mb-4">
                                        <div className={`tab-pane ${activeTab === 'bulkPayslips' ? 'active' : ''}`} id="bulkPayslips">
                                            <div className="row justify-content-center">
                                                <div className="col-lg-8 col-md-8">
                                                    <div className="input-group">
                                                        <div className="custom-file">
                                                            <input
                                                                type="file"
                                                                className="custom-file-input"
                                                                onChange={handleFileChange}
                                                            />
                                                            <label
                                                                className="custom-file-label"
                                                                htmlFor="digitalsignature"
                                                            >
                                                                {filename ? filename : "Choose file"}
                                                            </label>
                                                        </div>
                                                        <div className="input-group-append position-relative">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary custom-dropdown-btn"
                                                                onClick={handleUpload}
                                                                disabled={uploading}
                                                            >
                                                                {uploading ? 'Uploading...' : 'Upload'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`tab-pane ${activeTab === 'bulkInsurances' ? 'active' : ''}`} id="bulkInsurances">
                                            <div className="row justify-content-center">
                                                <div className="col-lg-8 col-md-8">
                                                    <div className="input-group">
                                                        <div className="custom-file">
                                                            <input
                                                                type="file"
                                                                className="custom-file-input"
                                                                onChange={handleFileChange}
                                                            />
                                                            <label
                                                                className="custom-file-label"
                                                                htmlFor="digitalsignature"
                                                            >
                                                                {filename ? filename : "Choose file"}
                                                            </label>
                                                        </div>
                                                        <div className="input-group-append position-relative">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary custom-dropdown-btn"
                                                                onClick={handleUpload}
                                                                disabled={uploading}
                                                            >
                                                                {uploading ? 'Uploading...' : 'Upload'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`tab-pane ${activeTab === 'bulkTaxes' ? 'active' : ''}`} id="bulkTaxes">
                                            <div className="row justify-content-center">
                                                <div className="col-lg-8 col-md-8">
                                                    <div className="input-group">
                                                        <div className="custom-file">
                                                            <input
                                                                type="file"
                                                                className="custom-file-input"
                                                                onChange={handleFileChange}
                                                            />
                                                            <label
                                                                className="custom-file-label"
                                                                htmlFor="digitalsignature"
                                                            >
                                                                {filename ? filename : "Choose file"}
                                                            </label>
                                                        </div>
                                                        <div className="input-group-append position-relative">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary custom-dropdown-btn"
                                                                onClick={handleUpload}
                                                                disabled={uploading}
                                                            >
                                                                {uploading ? 'Uploading...' : 'Upload'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div className="card ww-app-bg">
                                <div className="card-body">
                                    <span>
                                        <i className="ri-inbox-unarchive-fill"></i>
                                    </span>
                                </div>
                            </div>
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h4 className="mb-2">Recent Upload Details</h4>
                                    <div className="my-documents-list mt-4">
                                        <div className="row">
                                            {files.map((file, index) => (
                                                <div key={index} className="col-6 mb-3">
                                                    <div className="card shadow-none">
                                                        <div className="card-body">
                                                            <a href={file.url} className="stretched-link"></a>
                                                            <div className="my-files text-center">
                                                                <i className="ri-folder-zip-line"></i>
                                                                <h6 className="mb-0">{file.FileName}</h6>
                                                                <small className="text-muted d-block">
                                                                    Uploaded On
                                                                    <br /> {moment(file.UpdatedDate).format("DD-MM-YYYY")}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadCenter;
